import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getSavedFilters, saveFilters } from "@/lib/filterCookies";

// Shared precise device location ({lat,lng}) — set once via the locate button and used
// site-wide (search, follows, main list) so distances use precise coords, not just a ZIP.
const KEY = "tsi-myloc";
const listeners = new Set();

function readStore() {
    try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
let current = readStore();

function emit() { listeners.forEach((l) => l()); }
function subscribe(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

export function setMyLoc(loc) {
    current = loc && loc.lat != null && loc.lng != null ? { lat: loc.lat, lng: loc.lng } : null;
    try {
        if (current) localStorage.setItem(KEY, JSON.stringify(current));
        else localStorage.removeItem(KEY);
    } catch { /* ignore */ }
    emit();
}

export function useMyLoc() {
    const loc = useSyncExternalStore(subscribe, () => current, () => null);
    return [loc, setMyLoc];
}

// Request the device's precise location, store it, and back-fill the ZIP filter.
export function requestMyLocation() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) { toast.error("Location not supported on this device"); resolve(false); return; }
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setMyLoc(c);
                try {
                    const { data } = await api.get("/geo/reverse-zip", { params: c });
                    if (data?.zip) {
                        const saved = getSavedFilters({});
                        saveFilters({ ...saved, near_zip: data.zip });
                        toast.success(`Using your location · ${data.zip}`);
                    } else {
                        toast.success("Using your location");
                    }
                } catch { toast.success("Using your location"); }
                resolve(true);
            },
            () => { toast.error("Location permission denied"); resolve(false); },
            { timeout: 8000, enableHighAccuracy: true }
        );
    });
}

// Build request params that prefer precise coords, falling back to a ZIP.
export function geoParams(loc, zip) {
    if (loc && loc.lat != null && loc.lng != null) return { near_lat: loc.lat, near_lng: loc.lng };
    if (zip) return { near_zip: zip };
    return {};
}
